import RBush from "rbush";
import type { AABB2 } from "../aabb2Types";
import type { Point, Segment } from "./dcel";

interface SegmentBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  segmentIndex: number;
  start: ObjectPoint;
  end: ObjectPoint;
  startHotPixel: HotPixel | undefined;
  endHotPixel: HotPixel | undefined;
  hotPixels: HotPixel[]; // hot pixels intersecting this edge (or nothing)
}

interface ObjectPoint {
  x: number;
  y: number;
}

interface HotPixel extends ObjectPoint {
  x: number;
  y: number;
  index: number;
}

function addHotPixel(hotPixels: HotPixel[], x: number, y: number): HotPixel {
  const pixel = { x, y, index: hotPixels.length };
  hotPixels.push(pixel);
  return pixel;
}

type SegmentIsectParam = {
  t: number;
  pt: Point;
  vertexIndex: number;
};

export function buildSnapRoundSegments(
  segments: Segment[],
  segmentToPolygon: number[],
  constraintSegments: Segment[] = [],
  constraintPts: Point[] = [],
) {
  console.assert(segments.length === segmentToPolygon.length);

  let hotPixels: HotPixel[] = [];
  const segmentBoxes: SegmentBox[] = [];

  // convert all vertices into hot pixels, build segment boxes
  for (let i = 0; i < segmentToPolygon.length; i++) {
    const segment = segments[i];
    const [ax, ay, bx, by] = segment;

    const start = addHotPixel(hotPixels, ax, ay);
    const end = addHotPixel(hotPixels, bx, by);

    segmentBoxes.push({
      minX: Math.min(ax, bx),
      minY: Math.min(ay, by),
      maxX: Math.max(ax, bx),
      maxY: Math.max(ay, by),
      segmentIndex: i,
      start,
      end,
      startHotPixel: undefined,
      endHotPixel: undefined,
      hotPixels: [],
    });
  }

  // add all segments to an rtree
  const segmentTree = new RBush<SegmentBox>().load(segmentBoxes);

  // intersect all segments with one another, adding new hot pixels for intersections
  for (let i = 0; i < segmentBoxes.length; i++) {
    searchIntersections(segmentBoxes[i], segmentTree, hotPixels);
  }

  // Add intersections from constraintSegments: intersect them with existing segments
  for (const constraintSegment of constraintSegments) {
    const [constraintAx, constraintAy, constraintBx, constraintBy] = constraintSegment;
    const constraintSegmentBox: SegmentBox = {
      minX: Math.min(constraintAx, constraintBx),
      minY: Math.min(constraintAy, constraintBy),
      maxX: Math.max(constraintAx, constraintBx),
      maxY: Math.max(constraintAy, constraintBy),
      segmentIndex: -1, // Use -1 to indicate this is an intersection segment, not a regular segment
      start: { x: constraintAx, y: constraintAy },
      end: { x: constraintBx, y: constraintBy },
      startHotPixel: undefined,
      endHotPixel: undefined,
      hotPixels: [],
    };

    // Search for intersections with existing segments
    searchIntersections(constraintSegmentBox, segmentTree, hotPixels);
  }

  // Add intersection points from constraintPts as hot pixels
  // Note: Points that don't intersect any segments will still be added to vertices but won't be used
  // in any segmentIsectParams, so they won't affect the graph structure - they're just extra unused vertices
  for (const constraintPt of constraintPts) {
    addHotPixel(hotPixels, constraintPt[0], constraintPt[1]);
  }

  // lexicographically sort all hot pixels and remove all duplicates
  hotPixels = uniquePixels(hotPixels);
  hotPixels.forEach((x, i) => (x.index = i));

  // for each hot pixel, insert it into the corresponding edge it intersects
  for (let i = 0; i < hotPixels.length; i++) {
    handleHotPixel(hotPixels[i], segmentTree);
  }

  const segmentIsectParams: SegmentIsectParam[][] = [];

  for (let i = 0; i < segmentBoxes.length; i++) {
    segmentIsectParams.push(snapRoundEdge(segmentBoxes[i]));
  }

  const vertices: Point[] = hotPixels.map((x) => [x.x, x.y]);

  return { vertices, segmentIsectParams };
}

function uniquePixels(arr: HotPixel[]) {
  arr.sort(comparePixels);
  const result: HotPixel[] = [];
  for (let i = 0; i < arr.length; i++) {
    if (i === 0 || comparePixels(arr[i], arr[i - 1]) !== 0) {
      result.push(arr[i]);
    }
  }
  return result;
}

type SegmentBoxNode = SegmentBox & { children: SegmentBoxNode[]; leaf: boolean };

function handleHotPixel(p: HotPixel, edgeTree: RBush<SegmentBox>) {
  let node: SegmentBoxNode | undefined = (edgeTree as any).data as SegmentBoxNode;
  const nodesToSearch: SegmentBoxNode[] = [];

  while (node) {
    for (let i = 0; i < node.children.length; i++) {
      const q = node.children[i];

      if (pointInsideAABB2(p, q)) {
        if (!node.leaf) {
          // its not a leaf, so we won't add to hot pixel list
          nodesToSearch.push(q);
        } else if (q.start.x === p.x && q.start.y === p.y) {
          console.assert(!q.startHotPixel);
          q.startHotPixel = p;
        } else if (q.end.x === p.x && q.end.y === p.y) {
          console.assert(!q.endHotPixel);
          q.endHotPixel = p;
        } else if (edgeIntersectsPixel(q.start, q.end, p)) {
          // init hot pixel list and add pixel
          q.hotPixels = q.hotPixels || [];
          q.hotPixels.push(p);
        }
      }
    }
    node = nodesToSearch.pop();
  }
}

function snapRoundEdge(e: SegmentBox) {
  if (!e.hotPixels || !e.endHotPixel || !e.startHotPixel) {
    return [];
  }

  // sort hot pixels by pixel distance from the first edge point
  e.hotPixels.sort((a, b) => manhattanDist(e, a) - manhattanDist(e, b));

  const isects: SegmentIsectParam[] = [];

  isects.push({ t: 0, pt: [e.start.x, e.start.y], vertexIndex: e.startHotPixel.index });

  // insert hot points between edge endpoints
  for (let i = 0; i < e.hotPixels.length; i++) {
    const p = e.hotPixels[i];
    isects.push({ t: i + 1, pt: [p.x, p.y], vertexIndex: p.index });
  }

  isects.push({ t: e.hotPixels.length + 1, pt: [e.endHotPixel.x, e.endHotPixel.y], vertexIndex: e.endHotPixel.index });

  return isects;
}

function edgeIntersectsPixel(a: ObjectPoint, b: ObjectPoint, p: ObjectPoint): boolean {
  if ((p.x === a.x && p.y === a.y) || (p.x === b.x && p.y === b.y)) {
    console.assert(false);
    return false;
  }

  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const px = p.x - a.x;
  const py = p.y - a.y;

  if (dy !== 0 && p.x === a.x + Math.floor(0.5 + (dx * (py - 0.5)) / dy)) {
    return true;
  } // bottom x
  if (dy !== 0 && p.x === a.x + Math.floor(0.5 + (dx * (py + 0.5)) / dy)) {
    return true;
  } // top x
  if (dx !== 0 && p.y === a.y + Math.floor(0.5 + (dy * (px - 0.5)) / dx)) {
    return true;
  } // left y
  if (dx !== 0 && p.y === a.y + Math.floor(0.5 + (dy * (px + 0.5)) / dx)) {
    return true;
  } // right y

  return false;
}

function pointInsideAABB2(p: ObjectPoint, box: AABB2) {
  return p.x >= box.minX && p.x <= box.maxX && p.y >= box.minY && p.y <= box.maxY;
}

function searchIntersections(segment: SegmentBox, edgeTree: RBush<SegmentBox>, intersections: HotPixel[]) {
  let node: SegmentBoxNode | undefined = (edgeTree as any).data as SegmentBoxNode;
  const nodesToSearch: SegmentBoxNode[] = [];

  while (node) {
    for (let i = 0; i < node.children.length; i++) {
      const q = node.children[i];

      if (bboxIntersectsExact(segment, q)) {
        if (!node.leaf) {
          nodesToSearch.push(q);
        } else if (isNewIntersection(segment, q)) {
          findIntersection(segment.start, segment.end, q.start, q.end, intersections);
        }
      }
    }

    node = nodesToSearch.pop();
  }
}

function segmentsIntersect(a0: ObjectPoint, a1: ObjectPoint, b0: ObjectPoint, b1: ObjectPoint) {
  const x0 = area(a0, b0, b1);
  const y0 = area(a1, b0, b1);
  if ((x0 > 0 && y0 > 0) || (x0 < 0 && y0 < 0)) {
    return false;
  }

  const x1 = area(b0, a0, a1);
  const y1 = area(b1, a0, a1);
  if ((x1 > 0 && y1 > 0) || (x1 < 0 && y1 < 0)) {
    return false;
  }

  if (x0 === 0 && y0 === 0 && x1 === 0 && y1 === 0) {
    return false;
  } // collinear

  return true;
}

function bboxIntersectsExact(a: AABB2, b: AABB2) {
  return b.minX <= a.maxX && b.minY <= a.maxY && b.maxX >= a.minX && b.maxY >= a.minY;
}

function isNewIntersection(s: SegmentBox, q: SegmentBox) {
  // we actually do intersect subsequent pairs as this algorithm supports unstructured input and doesnt distinguish loops
  return (
    s.segmentIndex + 1 <= q.segmentIndex /* && s !== q.next */ && segmentsIntersect(s.start, s.end, q.start, q.end)
  );
}

function findIntersection(a: ObjectPoint, b: ObjectPoint, c: ObjectPoint, d: ObjectPoint, intersections: HotPixel[]) {
  const d1x = b.x - a.x;
  const d1y = b.y - a.y;
  const d2x = d.x - c.x;
  const d2y = d.y - c.y;
  const cross = d1x * d2y - d1y * d2x;
  const nom = (c.x - a.x) * d2y - (c.y - a.y) * d2x;
  const px = a.x + Math.floor(0.5 + (d1x * nom) / cross);
  const py = a.y + Math.floor(0.5 + (d1y * nom) / cross);

  if (
    (px === a.x && py === a.y) ||
    (px === b.x && py === b.y) ||
    (px === c.x && py === c.y) ||
    (px === d.x && py === d.y)
  ) {
    return;
  }

  addHotPixel(intersections, px, py);
}

// lexicographic point comparison
function comparePixels(a: ObjectPoint, b: ObjectPoint) {
  return a.x - b.x || a.y - b.y;
}

// calculate signed area of a triangle
function area(p: ObjectPoint, q: ObjectPoint, r: ObjectPoint) {
  return (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
}

// Manhattan distance between two points
function manhattanDist(a: SegmentBox, b: HotPixel) {
  return Math.abs(a.start.x - b.x) + Math.abs(a.start.y - b.y);
}
